import { useEffect, useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import { Button, Dialog, Input, Select } from '@/design-system';
import { isStrongPassword } from '@/features/auth/components/passwordPolicy';
import { PasswordRequirements } from '@/features/auth/components/PasswordRequirements';
import type { StaffUser, UpdateStaffUserData } from '../api/users.service';

type EditForm = {
  name: string;
  email: string;
  password: string;
  role: StaffUser['role'];
};

interface UserEditDialogProps {
  isOpen: boolean;
  user: StaffUser | null;
  onClose: () => void;
  updateUser: UseMutationResult<unknown, unknown, { id: string; data: UpdateStaffUserData }, unknown>;
  currentUserRole: StaffUser['role'] | undefined;
}

export function UserEditDialog({ isOpen, user, onClose, updateUser, currentUserRole }: UserEditDialogProps) {
  const [formData, setFormData] = useState<EditForm>({
    name: '',
    email: '',
    password: '',
    role: 'SUPPORT',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user || !isOpen) return;
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
    });
    setFormErrors({});
  }, [user, isOpen]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Nome é obrigatório';
    else if (formData.name.length < 2) errors.name = 'Nome deve ter no mínimo 2 caracteres';
    if (!formData.email.trim()) errors.email = 'Email é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Email inválido';
    if (formData.password && !isStrongPassword(formData.password))
      errors.password = 'A senha não atende à política de segurança.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleClose = () => {
    setFormErrors({});
    onClose();
  };

  const handleSave = async () => {
    if (!user || !validateForm()) return;
    const updateData: UpdateStaffUserData = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
    };
    if (formData.password) updateData.password = formData.password;
    try {
      await updateUser.mutateAsync({ id: user.id, data: updateData });
      handleClose();
    } catch {
      /* toast in mutation */
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} title="Editar Usuário" size="md" footer={(
      <div className="flex justify-end gap-3">
        <Button variant="secondary" onClick={handleClose} disabled={updateUser.isPending}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={() => void handleSave()} isLoading={updateUser.isPending}>
          Salvar
        </Button>
      </div>
    )}>
      <div className="space-y-4">
        <Input
          label="Nome"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={formErrors.name}
          required
        />
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
          error={formErrors.email}
          required
        />
        <div>
          <Input
            label="Nova Senha (deixe vazio para manter a atual)"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
            error={formErrors.password}
          />
          {formData.password.length > 0 && <PasswordRequirements value={formData.password} />}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">Perfil</label>
          <Select
            value={formData.role}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, role: e.target.value as StaffUser['role'] }))
            }
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={currentUserRole !== 'SUPERADMIN'}
          >
            <option value="SUPPORT">Suporte</option>
            <option value="FINANCE">Financeiro</option>
            <option value="SALES">Vendas</option>
            {currentUserRole === 'SUPERADMIN' && <option value="SUPERADMIN">Super Admin</option>}
          </Select>
        </div>
      </div>
    </Dialog>
  );
}
